import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

import { Image } from '#features/ui';

const LoopSvg = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 2l4 4-4 4" />
    <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
    <path d="M7 22l-4-4 4-4" />
    <path d="M21 13v1a4 4 0 0 1-4 4H3" />
  </svg>
);

type Tone = 'muted' | 'red' | 'loop';

const dotColor = (tone: Tone) =>
  tone === 'red' ? '#ef4444' : tone === 'loop' ? '#8E7BFF' : 'rgba(255,255,255,0.4)';

type EventProps = {
  time: string;
  label: string;
  tone: Tone;
  loop?: boolean;
  delay: number;
};

const Event = ({ time, label, tone, loop, delay }: EventProps) => (
  <Flex gap={4} alignItems="center" py={2.5} position="relative" asChild>
    <m.div
      initial={{ opacity: 0, x: 16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.4 }}
    >
      <Flex w="42px" flexShrink={0} justifyContent="center" zIndex={2}>
        <Box
          w="12px"
          h="12px"
          borderRadius="full"
          bg={dotColor(tone)}
          border="3px solid"
          borderColor="hsl(228, 11.6%, 9%)"
          style={{ boxShadow: `0 0 10px ${dotColor(tone)}` }}
        />
      </Flex>
      <Text
        fontSize="sm"
        fontWeight="800"
        color="fg.subtle"
        w="46px"
        flexShrink={0}
        fontVariantNumeric="tabular-nums"
      >
        {time}
      </Text>
      <Flex alignItems="center" gap={2} flex="1">
        <Text
          fontSize="sm"
          fontWeight="700"
          color={tone === 'red' ? '#fca5a5' : tone === 'loop' ? 'purple.200' : 'fg.muted'}
        >
          {label}
        </Text>
        {loop && (
          <Box color="purple.300" asChild>
            <m.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
              <LoopSvg />
            </m.div>
          </Box>
        )}
      </Flex>
    </m.div>
  </Flex>
);

const TimelineCard = ({
  title,
  repeat,
  steps,
}: {
  title: string;
  repeat: string;
  steps: { time: string; label: string; tone: Tone; loop?: boolean }[];
}) => (
  <Box
    w="100%"
    maxW="430px"
    borderRadius="2xl"
    p={{ base: 5, md: 6 }}
    border="1px solid"
    borderColor="border.subtle"
    background="linear-gradient(165deg, hsla(228,11.6%,14%,0.9) 0%, hsla(228,11.6%,9%,0.7) 100%)"
    style={{ boxShadow: '0 24px 60px -20px rgba(0,0,0,0.6)' }}
  >
    <Flex alignItems="center" justifyContent="space-between" mb={4}>
      <Text fontSize="sm" fontWeight="800" color="fg.emphasized">
        {title}
      </Text>
      <Flex alignItems="center" gap={1.5} color="purple.300">
        <LoopSvg />
        <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
          {repeat}
        </Text>
      </Flex>
    </Flex>

    <Box position="relative">
      {/* Connector line behind the dots (dots sit in a 42px column, center = 21px) */}
      <Box
        position="absolute"
        left="21px"
        top="14px"
        bottom="14px"
        w="2px"
        bg="bg.muted"
        transform="translateX(-50%)"
      />
      {/* Running highlight travelling down the line, forever */}
      <Box position="absolute" left="21px" top="0" transform="translateX(-50%)" zIndex={1} asChild>
        <m.div
          animate={{ top: ['6%', '92%'], opacity: [0, 1, 1, 0] }}
          transition={{
            duration: 3.4,
            repeat: Infinity,
            ease: 'easeInOut',
            times: [0, 0.12, 0.88, 1],
          }}
        >
          <Box
            w="10px"
            h="10px"
            borderRadius="full"
            bg="#8E7BFF"
            style={{ boxShadow: '0 0 16px 4px rgba(142,123,255,0.7)' }}
          />
        </m.div>
      </Box>

      {steps.map((step, i) => (
        <Event
          key={step.time}
          time={step.time}
          label={step.label}
          tone={step.tone}
          loop={step.loop}
          delay={i * 0.12}
        />
      ))}
    </Box>
  </Box>
);

type ValuePropProps = {
  image?: string | null;
  accentColor?: string;
  /** Game name - makes the pain headline game-specific on game landings. */
  gameName?: string | null;
};

const isVisibleColor = (color?: string) => {
  if (!color) return false;
  const c = color.toLowerCase();
  return c !== '#000000' && c !== '#000' && c !== 'black' && c !== 'transparent';
};

/**
 * The "problem" beat of the landing - a solo gamer's night on repeat
 * (queue → toxic randoms → defeat → queue again), so the next section
 * (How It Works) reads as the answer. The benefit cards that used to live here
 * moved into Features to kill the overlap.
 */
export const ValueProp = ({ image, accentColor, gameName }: ValuePropProps) => {
  const { t } = useLingui();
  const glowColor = isVisibleColor(accentColor) ? accentColor! : '#7C3AED';

  const steps: { time: string; label: string; tone: Tone; loop?: boolean }[] = [
    { time: '20:00', label: t`v3.pain.step1`, tone: 'muted' },
    { time: '20:14', label: t`v3.pain.step2`, tone: 'red' },
    { time: '20:38', label: t`v3.pain.step3`, tone: 'red' },
    { time: '21:00', label: t`v3.pain.step4`, tone: 'loop', loop: true },
  ];

  return (
    <Box as="section" aria-label="The problem" w="100%" py={{ base: 10, md: 16 }}>
      <Flex
        flexDirection={{ base: 'column', lg: 'row' }}
        alignItems="center"
        gap={{ base: 6, lg: 16 }}
      >
        {/* Lonely gamer art from admin */}
        {image && (
          <Box
            position="relative"
            /*
              No fixed height: the art is near-square, so a tall box + objectFit:contain scaled it
              down to the box width and banked all the leftover space on top.

              A reserved SQUARE, though, is not the same thing as a fixed height. Without one the
              box is zero pixels tall until the art loads - the img carries a width and no height,
              and CSS height:auto leaves the browser nothing to derive a ratio from - so the whole
              section below jumps once it arrives. Same trick the LFG banners already use.
            */
            w={{ base: '300px', lg: '380px' }}
            maxW="100%"
            style={{ aspectRatio: '1 / 1' }}
            flexShrink={0}
            order={{ base: 2, lg: 1 }}
            mx={{ base: 'auto', lg: 0 }}
            asChild
          >
            <m.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="140%"
                h="140%"
                borderRadius="full"
                background={`radial-gradient(circle closest-side, ${glowColor}1a 0%, ${glowColor}0a 45%, transparent 100%)`}
                pointerEvents="none"
              />
              <Image
                src={image}
                alt="gamer character"
                width={380}
                style={{
                  objectFit: 'contain',
                  objectPosition: 'bottom center',
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  // Fade the bottom edge of the character into the section background
                  // so it blends instead of ending on a hard cut.
                  WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 72%, transparent 98%)',
                  maskImage: 'linear-gradient(to bottom, #000 0%, #000 72%, transparent 98%)',
                }}
              />
            </m.div>
          </Box>
        )}

        {/* Content */}
        <Box flex="1" order={{ base: 1, lg: 2 }} w="100%">
          <Text
            fontSize="xs"
            fontWeight="700"
            color="purple.300"
            textTransform="uppercase"
            letterSpacing="0.15em"
            mb={3}
          >
            {t`v3.pain.label`}
          </Text>
          <Heading
            fontWeight="900"
            fontSize={{ base: '2xl', md: '4xl' }}
            lineHeight="1.1"
            as="h2"
            maxW="520px"
            textTransform="uppercase"
            letterSpacing="-0.02em"
          >
            {gameName ? t`Every ${gameName} night ends the same` : t`v3.pain.title`}
          </Heading>
          <Text fontSize="md" color="fg.muted" lineHeight="1.7" maxW="500px" mt={4} mb={8}>
            {t`v3.pain.subtitle`}
          </Text>

          <TimelineCard title={t`v3.pain.timelineTitle`} repeat={t`v3.pain.repeat`} steps={steps} />

          <Flex alignItems="center" gap={2} mt={6}>
            <Box w="20px" h="1px" bg="purple.400" />
            <Text fontSize="sm" fontWeight="700" color="purple.200">
              {t`v3.pain.bridge`}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};
